import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { ZodObject } from "zod";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { useSettings } from "../../hooks";

export function SettingsGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="border rounded-lg p-4">
      <Heading displayLevel={3}>{title}</Heading>
      {description && <Text muted>{description}</Text>}
      <div className="flex flex-col gap-2 mt-3">{children}</div>
    </div>
  );
}

export function SettingsGroupForm({
  title,
  description,
  children,
  schema,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic schema
  schema: ZodObject<any>;
}) {
  const { settings } = useSettings();
  const utils = trpc.useUtils();

  const form = useForm({
    defaultValues: settings,
    resolver: zodResolver(schema),
  });

  const mutation = trpc.settings.update.useMutation();

  const onSubmit = async (values: object) => {
    await mutation.mutateAsync(
      Object.keys(values).map((name) => ({ name, value: values[name] })),
      {
        onSuccess() {
          void utils.settings.list.invalidate();
          toast.success("Settings updated");
        },
        onError(err) {
          toast.error("Error updating settings", {
            description: err.message,
          });
        },
      },
    );
  };

  return (
    <SettingsGroup title={title} description={description}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>{children}</FormProvider>
        <div className="flex justify-end mt-3">
          <Button type="submit" isPending={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </SettingsGroup>
  );
}
